import React, { Component } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Typeahead } from 'react-bootstrap-typeahead';
import { toast } from 'react-toastify';

// Validation schema
const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  biography: Yup.string().required('Biography is required'),
  artist: Yup.array().min(1, 'Please select an artist'),
  instrument: Yup.array().min(1, 'Please select an instrument'),
});

class CreateMember extends Component {
  constructor(props) {
    super(props);

    // Initialize state
    this.state = {
      photo: '',
      file: null,
      awsKeys: {},
      disableUpload: false,
      photoUpload: false,
      isUploading: false,
    };

    // Initialize form values based on mode
    if (props.memberInfo && props.memberInfo.mode === 'EDIT') {
      const { initialValues, instruments, artists } = props.memberInfo;

      let artist = [];
      let instrument = [];

      if (instruments && initialValues) {
        instruments.forEach(instrumentInfo => {
          if (instrumentInfo.id === initialValues.instrument_id) {
            instrument[0] = instrumentInfo;
          }
        });
      }

      if (artists && initialValues) {
        artists.forEach(artistInfo => {
          if (artistInfo.id === initialValues.artist_id) {
            artist[0] = artistInfo;
          }
        });
      }

      this.initialValues = {
        name: (initialValues && initialValues.name) || '',
        biography: (initialValues && initialValues.biography) || '',
        artist: artist,
        instrument: instrument,
      };

      // Update state with photo if in edit mode
      if (initialValues && initialValues.photo) {
        this.state.photo = initialValues.photo;
        this.state.photoUpload = true;
      }
    } else {
      // Default values for create mode
      this.initialValues = {
        name: '',
        biography: '',
        artist: [],
        instrument: [],
      };
    }
  }

  handleSubmit = (values, { setSubmitting }) => {
    const { artist, instrument } = values;
    const artist_id = artist[0] ? artist[0].id : null;
    const instrument_id = instrument[0] ? instrument[0].id : null;

    if (this.props.memberInfo && this.props.memberInfo.createMember) {
      this.props.memberInfo.createMember({
        artist_id,
        instrument_id,
        photo: this.state.photo,
        biography: values.biography,
        name: values.name,
      });
    }

    setSubmitting(false);
  };

  onFileChange = async (eve) => {
    this.setState({
      photoUpload: false,
      disableUpload: false,
    });

    const file = eve.target.files[0];
    if (!file) return;

    const getAwsKey = this.props.memberInfo && this.props.memberInfo.getAwsKey;
    if (!getAwsKey) {
      toast.error('Unable to get AWS key function');
      return;
    }

    const fileName = file.name.replace(/ /g, '_');

    try {
      const awsKeys = await getAwsKey({ key: `members/images/${fileName}` });

      this.setState({
        file,
        photo: `${awsKeys.action}${awsKeys.key}`,
        awsKeys,
        disableUpload: true,
      });
    } catch (error) {
      toast.error('Error getting AWS keys');
      console.error(error);
    }
  };

  uploadPhoto = async () => {
    if (!this.state.awsKeys.action || !this.state.file) {
      toast.error('Please select a file first.');
      return;
    }

    const formData = new FormData();
    formData.append('key', this.state.awsKeys.key || 'NA');
    formData.append('acl', this.state.awsKeys.acl || 'NA');
    formData.append('X-Amz-Credential', this.state.awsKeys['x-amz-credential'] || 'NA');
    formData.append('X-Amz-Algorithm', this.state.awsKeys['x-amz-algorithm'] || 'NA');
    formData.append('X-Amz-Date', this.state.awsKeys['x-amz-date'] || 'NA');
    formData.append('Policy', this.state.awsKeys.policy || 'NA');
    formData.append('X-Amz-Signature', this.state.awsKeys['x-amz-signature'] || 'NA');
    formData.append('file', this.state.file);

    try {
      this.setState({ isUploading: true });

      const response = await fetch(this.state.awsKeys.action, {
        method: 'POST',
        body: formData
      });

      if (response.ok || response.status === 204) {
        toast.success('Photo uploaded successfully.');
        this.setState({
          photoUpload: true,
          isUploading: false,
        });
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      toast.error('Something went wrong while uploading photo.');
      this.setState({ isUploading: false });
      console.error(error);
    }
  };

  // Custom Field Components
  CustomInput = ({ field, form, accessMode, ...props }) => {
    // accessMode is extracted and not passed to the input element
    const hasError = form.touched[field.name] && form.errors[field.name];
    return (
      <div>
        <input
          {...field}
          {...props}
          className={`form-control ${accessMode === 'view' ? 'disabled' : ''} ${hasError ? 'error' : ''}`}
        />
        {hasError && (
          <div className="error-message" style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
            {form.errors[field.name]}
          </div>
        )}
      </div>
    );
  };

  TypeaheadField = ({ field, form, options, labelKey, placeholder, accessMode, ...props }) => {
    const hasError = form.touched[field.name] && form.errors[field.name];
    // accessMode is already extracted from props, so it won't be passed to Typeahead

    return (
      <div>
        <Typeahead
          onChange={selected => {
            form.setFieldValue(field.name, selected);
          }}
          labelKey={labelKey}
          options={options || []}
          selected={field.value || []}
          placeholder={placeholder}
          className={`${accessMode === 'view' ? 'disabled' : ''}`}
          disabled={accessMode === 'view'}
          {...props}
        />
        {hasError && (
          <div className="error-message" style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>
            {form.errors[field.name]}
          </div>
        )}
      </div>
    );
  };

  render() {
    const memberInfo = this.props.memberInfo || {};
    const { isCreating, artists, instruments } = memberInfo;
    const { accessMode } = this.props;
    const { disableUpload, isUploading, photoUpload } = this.state;

    return (
      <section className="well create-piece">
        <Formik
          initialValues={this.initialValues}
          validationSchema={validationSchema}
          onSubmit={this.handleSubmit}
          enableReinitialize={true}
        >
          {({ values, setFieldValue, isSubmitting, isValid, dirty }) => (
            <Form>
              <div className="row">
                <div className="col-md-3">
                  <label>Name</label>
                </div>
                <div className="col-md-9">
                  <Field
                    name="name"
                    type="text"
                    placeholder="Name"
                    component={this.CustomInput}
                    accessMode={accessMode}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-3">
                  <label>Biography</label>
                </div>
                <div className="col-md-9">
                  <Field
                    name="biography"
                    type="text"
                    placeholder="Biography"
                    component={this.CustomInput}
                    accessMode={accessMode}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-3">
                  <label>Artist</label>
                </div>
                <div className="col-md-9">
                  <Field
                    name="artist"
                    component={this.TypeaheadField}
                    options={artists || []}
                    labelKey="title"
                    placeholder="Artist"
                    accessMode={accessMode}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-3">
                  <label>Instrument</label>
                </div>
                <div className="col-md-9">
                  <Field
                    name="instrument"
                    component={this.TypeaheadField}
                    options={instruments || []}
                    labelKey="name"
                    placeholder="Instrument"
                    accessMode={accessMode}
                  />
                </div>
              </div>

              {accessMode === 'edit' && (
                <>
                  <div className="row">
                    <div className="col-md-3">
                      <label>Photo</label>
                    </div>
                    <div className="col-md-9">
                      <input
                        type="file"
                        name="file"
                        accept="image/*"
                        onChange={this.onFileChange}
                      />
                      {this.state.file && (
                        <div style={{ marginTop: '10px' }}>
                          <button
                            type="button"
                            className="btn btn-secondary w-100"
                            onClick={this.uploadPhoto}
                            disabled={!disableUpload || isUploading || photoUpload}
                          >
                            {isUploading ? 'Uploading...' : photoUpload ? 'Photo Uploaded ✓' : 'Upload to Amazon S3'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="row" style={{ marginTop: '20px' }}>
                    <div className="col-md-12">
                      <div className="create-piece__btn-container">
                        <button
                          className="btn btn-primary btn-bg w-100"
                          type="submit"
                          disabled={
                            isCreating ||
                            isSubmitting ||
                            !isValid ||
                            !photoUpload ||
                            !values.name ||
                            !values.biography ||
                            !values.artist ||
                            values.artist.length === 0 ||
                            !values.instrument ||
                            values.instrument.length === 0
                          }
                        >
                          {isCreating || isSubmitting ? (
                            <span className="spinner">Processing...</span>
                          ) : (
                            <span>{this.props.memberInfo?.mode === 'EDIT' ? 'Update Member' : 'Create Member'}</span>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </Form>
          )}
        </Formik>
      </section>
    );
  }
}

export default CreateMember;
